import { Claim } from '../api/claims';
import { ClientGroup, ClientGroupStats } from '../types/clientGroup';

/**
 * Groups claims by client email address
 */
export function groupClaimsByClient(claims: Claim[]): ClientGroup[] {
  const groupMap = new Map<string, ClientGroup>();
  const creditDateSets = new Map<string, Set<string>>();

  claims.forEach(claim => {
    const email = claim.email || 'unknown';

    if (!groupMap.has(email)) {
      groupMap.set(email, {
        email,
        clientName: `${claim.firstName || ''} ${claim.lastName || ''}`.trim() || 'Unknown Client',
        userId: claim.userId,
        clientId: claim.clientId,
        claims: [],
        totalClaims: 0,
        activeClaims: 0,
        cancelledClaims: 0,
        completedClaims: 0,
        locSentClaims: 0,
        bulkSubmittedClaims: 0,
        responseExceptionClaims: 0,
        auditedClaims: 0,
        uniqueCreditDates: 0,
        mostRecentActivity: null,
        expanded: false
      });
      creditDateSets.set(email, new Set());
    }

    const group = groupMap.get(email)!;
    group.claims.push(claim);
    group.totalClaims++;

    // Track unique credit start dates
    if (claim.craCreditStartDate) {
      creditDateSets.get(email)!.add(claim.craCreditStartDate);
    }

    // Update statistics
    if (claim.status === 'CANCELLED') {
      group.cancelledClaims++;
    } else if (claim.status === 'COMPLETED') {
      group.completedClaims++;
    } else {
      group.activeClaims++;
    }

    if (claim.locSentDate) {
      group.locSentClaims++;
    }

    // Check if claim was bulk submitted
    if (claim.submissionSent) {
      group.bulkSubmittedClaims++;
    }

    // Lender response landed as an exception (multi-rep, withdrawn, no
    // records found…) — anything but 'Received'.
    if (claim.submissionOutcome && claim.submissionOutcome !== 'Received') {
      group.responseExceptionClaims++;
    }

    // Check if claim has been audited - we'll check for audit-related fields
    if (claim.hasAudit || (claim.auditCount && claim.auditCount > 0) || (claim.audits && claim.audits.length > 0)) {
      group.auditedClaims++;
    }

    // Update most recent activity
    const claimDate = claim.createdInApiAt ? new Date(claim.createdInApiAt) : null;
    if (claimDate && (!group.mostRecentActivity || claimDate > group.mostRecentActivity)) {
      group.mostRecentActivity = claimDate;
    }

    // Update client name if current claim has a better one
    const currentName = `${claim.firstName || ''} ${claim.lastName || ''}`.trim();
    if (currentName && currentName !== 'Unknown Client' && group.clientName === 'Unknown Client') {
      group.clientName = currentName;
    }
  });

  // Set unique credit date counts
  groupMap.forEach((group, email) => {
    group.uniqueCreditDates = creditDateSets.get(email)?.size ?? 0;
  });

  // Sort groups by most recent activity
  return Array.from(groupMap.values()).sort((a, b) => {
    if (!a.mostRecentActivity) return 1;
    if (!b.mostRecentActivity) return -1;
    return b.mostRecentActivity.getTime() - a.mostRecentActivity.getTime();
  });
}

/**
 * Calculate overall statistics for client groups
 */
export function calculateGroupStats(groups: ClientGroup[]): ClientGroupStats {
  return groups.reduce((stats, group) => ({
    totalClients: stats.totalClients + 1,
    totalClaims: stats.totalClaims + group.totalClaims,
    totalActive: stats.totalActive + group.activeClaims,
    totalCancelled: stats.totalCancelled + group.cancelledClaims,
    totalCompleted: stats.totalCompleted + group.completedClaims
  }), {
    totalClients: 0,
    totalClaims: 0,
    totalActive: 0,
    totalCancelled: 0,
    totalCompleted: 0
  });
}

/**
 * Filter client groups based on search term
 */
export function filterClientGroups(groups: ClientGroup[], searchTerm: string): ClientGroup[] {
  if (!searchTerm) return groups;

  const normalizedSearch = searchTerm.toLowerCase().trim();
  
  return groups.filter(group => {
    // Check client-level fields
    if (group.email.toLowerCase().includes(normalizedSearch) ||
        group.clientName.toLowerCase().includes(normalizedSearch)) {
      return true;
    }

    // Check if any claims match
    return group.claims.some(claim => {
      const claimSearchableText = [
        claim.id,
        claim.firstName,
        claim.lastName,
        claim.email,
        claim.phoneNumber,
        claim.postcode,
        claim.craFinanceLender
      ].filter(Boolean).join(' ').toLowerCase();

      return claimSearchableText.includes(normalizedSearch);
    });
  });
}

/**
 * Get display status for a claim
 */
export function getClaimDisplayStatus(claim: Claim): string {
  if (claim.status === 'CANCELLED') {
    return 'Cancelled';
  }
  // Check for bulk submission (takes precedence over LOC sent)
  if (claim.submissionSent) {
    // Once the lender has replied to the bulk submission (an imported
    // response sets submissionOutcome), say so rather than 'Bulk Submitted'.
    return claim.submissionOutcome || claim.responseDate ? 'Response received' : 'Bulk Submitted';
  }
  if (!claim.locSentDate) {
    return 'LOC needs sending';
  }
  if (claim.locSentDate && !claim.responseDate) {
    return 'LOC Sent';
  }
  if (claim.responseDate) {
    return 'Response received';
  }
  return claim.status || 'Unknown';
}